<div class="dash-shell">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/dashboard">Admin</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <a class="page-head__crumb" routerLink="/admin/system-status">System status</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      <span class="page-head__crumb page-head__crumb--current" aria-current="page"
        >Database browser</span
      >
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles">
        <h1 class="page-head__title">Database browser</h1>
        <p class="page-head__subtitle">
          Read-only view of every application table. Pick a table to browse its rows, search across
          text columns, and click a column header to sort.
        </p>
      </div>
    </div>
  </header>

  <!-- ── Export / import ─────────────────────────────────────────────────── -->
  <section class="db-transfer" aria-labelledby="db-transfer-heading">
    <div class="db-transfer__intro">
      <h2 id="db-transfer-heading" class="db-transfer__title">Export and import</h2>
      <p class="db-transfer__subtitle">
        Move the whole database between environments as a zip archive — typically export from
        staging and import into a local stack. The archive holds every table's rows but not the
        uploaded documents they point at.
      </p>
    </div>

    <div class="db-transfer__cards">
      <mat-card appearance="outlined" class="db-transfer__card">
        <mat-card-content>
          <h3 class="db-transfer__card-title">Export</h3>
          <p class="db-transfer__card-desc">
            Downloads every table as one archive. It contains personal information and password
            hashes, so treat the file as confidential. Each export is logged.
          </p>
          <button
            mat-flat-button
            color="primary"
            type="button"
            class="db-transfer__action"
            (click)="exportDatabase()"
            [disabled]="exporting()"
          >
            @if (exporting()) {
              <mat-spinner diameter="18" />
              Exporting…
            } @else {
              <app-icon name="download" [size]="16" aria-hidden="true" />
              Export database
            }
          </button>
          @if (exportError(); as e) {
            <div class="db-transfer__result db-transfer__result--error" role="alert">
              <p class="db-transfer__result-line">{{ e }}</p>
            </div>
          }
        </mat-card-content>
      </mat-card>

      <mat-card appearance="outlined" class="db-transfer__card">
        <mat-card-content>
          <h3 class="db-transfer__card-title">Import</h3>
          <p class="db-transfer__card-desc">
            Replaces every table with an export archive. Refused on production, and on staging
            unless seeding is switched on. You will need to sign in again afterwards.
          </p>
          <div class="db-transfer__file">
            <input
              #importInput
              class="db-transfer__file-input"
              type="file"
              accept=".zip,application/zip"
              aria-label="Export archive to import"
              tabindex="-1"
              (change)="onImportFileSelected($event)"
            />
            <button
              mat-stroked-button
              type="button"
              (click)="importInput.click()"
              [disabled]="importing()"
            >
              Choose archive
            </button>
            <span class="db-transfer__file-name" aria-live="polite">
              {{ importFile()?.name | dash }}
            </span>
          </div>
          <button
            mat-flat-button
            color="warn"
            type="button"
            class="db-transfer__action"
            (click)="importDatabase(importInput)"
            [disabled]="importing() || !importFile()"
          >
            @if (importing()) {
              <mat-spinner diameter="18" />
              Importing…
            } @else {
              <app-icon name="upload" [size]="16" aria-hidden="true" />
              Import database
            }
          </button>

          @if (importResult(); as r) {
            <div
              class="db-transfer__result"
              [class.db-transfer__result--ok]="r.status === 'ok'"
              [class.db-transfer__result--error]="r.status === 'error'"
              [attr.role]="r.status === 'error' ? 'alert' : 'status'"
            >
              <p class="db-transfer__result-line">
                <span class="db-transfer__result-badge">{{ r.status }}</span>
                {{ r.detail }}
              </p>
              @if (r.status === 'ok' && r.meta.exported_at) {
                <p class="db-transfer__result-meta">
                  Exported from <code>{{ r.meta.environment }}</code> at
                  <code>{{ r.meta.exported_at }}</code>
                  @if (r.meta.release) {
                    (release <code>{{ r.meta.release }}</code>)
                  }
                </p>
              }
              @if (r.meta.extra_migrations?.length) {
                <p class="db-transfer__result-meta">
                  This database is ahead of the export by
                  {{ r.meta.extra_migrations!.length }} migration{{
                    r.meta.extra_migrations!.length === 1 ? '' : 's'
                  }}: {{ r.meta.extra_migrations!.join(', ') }}
                </p>
              }
            </div>
          }
        </mat-card-content>
      </mat-card>
    </div>
  </section>

  <div class="db-layout">
    <!-- ── Left rail: table list ──────────────────────────────────────────── -->
    <aside class="db-tables" aria-label="Tables">
      <mat-form-field appearance="outline" class="db-tables__filter">
        <mat-label>Filter tables</mat-label>
        <input
          matInput
          type="text"
          [value]="tableFilter()"
          (input)="onTableFilter($any($event.target).value)"
          autocomplete="off"
        />
      </mat-form-field>

      @if (tablesLoading()) {
        <ul class="table-list" aria-label="Loading tables" aria-busy="true">
          @for (r of [1, 2, 3, 4, 5, 6, 7, 8]; track r) {
            <li>
              <div class="table-list__item">
                <span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span>
                <span class="sg-skeleton sg-skeleton--text sg-skeleton--w50"></span>
              </div>
            </li>
          }
        </ul>
      } @else if (tablesError()) {
        <div class="sg-empty sg-empty--error sg-empty--bordered">
          <app-icon name="x-circle" [size]="32" />
          <p class="sg-empty__title">Couldn't load tables</p>
          <p class="sg-empty__desc">Something went wrong on our side.</p>
          <button mat-stroked-button type="button" (click)="loadTables()">
            <app-icon name="refresh" [size]="16" /> Try again
          </button>
        </div>
      } @else {
        <ul class="table-list">
          @for (t of filteredTables(); track t.key) {
            <li>
              <button
                type="button"
                class="table-list__item"
                [class.table-list__item--active]="t.key === selectedKey()"
                (click)="selectTable(t.key)"
              >
                <span class="table-list__name">{{ t.table }}</span>
                <span class="table-list__meta">
                  {{ t.app_label }} · {{ t.row_count }} row{{ t.row_count === 1 ? '' : 's' }}
                </span>
              </button>
            </li>
          } @empty {
            <li class="table-list__empty">No tables match the filter.</li>
          }
        </ul>
      }
    </aside>

    <!-- ── Right pane: rows ───────────────────────────────────────────────── -->
    <section class="db-data" aria-label="Rows">
      @if (!selectedKey()) {
        <div class="sg-empty sg-empty--bordered">
          <app-icon name="database" [size]="40" />
          <p class="sg-empty__title">No table selected</p>
          <p class="sg-empty__desc">Pick a table on the left to browse its rows.</p>
        </div>
      } @else {
        <div class="db-data__controls">
          <mat-form-field appearance="outline" class="db-data__search">
            <mat-label>Search rows</mat-label>
            <input
              matInput
              type="text"
              placeholder="Search text columns…"
              (keyup.enter)="onSearch($any($event.target).value)"
              autocomplete="off"
            />
          </mat-form-field>
          <button mat-stroked-button type="button" (click)="retryRows()" [disabled]="rowsLoading()">
            Refresh
          </button>
        </div>

        @if (rowsLoading()) {
          <div class="db-table-wrap" aria-label="Loading rows" aria-busy="true">
            <table class="db-table">
              <thead>
                <tr>
                  @for (c of [1, 2, 3, 4, 5]; track c) {
                    <th scope="col">
                      <span class="sg-skeleton sg-skeleton--text sg-skeleton--w70"></span>
                    </th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (r of [1, 2, 3, 4, 5, 6, 7, 8]; track r) {
                  <tr>
                    @for (c of [1, 2, 3, 4, 5]; track c) {
                      <td>
                        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span>
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else if (rowsError()) {
          <div class="sg-empty sg-empty--error sg-empty--bordered">
            <app-icon name="x-circle" [size]="40" />
            <p class="sg-empty__title">Couldn't load rows</p>
            <p class="sg-empty__desc">Something went wrong on our side.</p>
            <button mat-stroked-button type="button" (click)="retryRows()">
              <app-icon name="refresh" [size]="18" /> Try again
            </button>
          </div>
        } @else if (rows(); as d) {
          @if (d.results.length === 0) {
            <div class="sg-empty sg-empty--bordered">
              <app-icon name="search" [size]="40" />
              <p class="sg-empty__title">No results</p>
              <p class="sg-empty__desc">No rows match the current search.</p>
            </div>
          } @else {
            <div class="db-table-wrap">
              <table class="db-table">
                <thead>
                  <tr>
                    @for (col of d.columns; track col) {
                      <th scope="col">
                        <button
                          type="button"
                          class="db-table__sort"
                          (click)="sortBy(col)"
                          [attr.aria-sort]="
                            sortDirection(col) === 'asc'
                              ? 'ascending'
                              : sortDirection(col) === 'desc'
                                ? 'descending'
                                : 'none'
                          "
                        >
                          {{ col }}
                          @if (sortDirection(col) === 'asc') {
                            <app-icon name="chevron-up" [size]="12" aria-hidden="true" />
                          } @else if (sortDirection(col) === 'desc') {
                            <app-icon name="chevron-down" [size]="12" aria-hidden="true" />
                          }
                        </button>
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of d.results; track $index) {
                    <tr>
                      @for (col of d.columns; track col) {
                        <td [title]="row[col] ?? ''">{{ row[col] | dash }}</td>
                      }
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            @if (d.num_pages > 1) {
              <div class="db-pager">
                <button
                  mat-stroked-button
                  type="button"
                  (click)="previousPage()"
                  [disabled]="!d.has_previous"
                >
                  Previous
                </button>
                <span class="db-pager__status">
                  Page {{ d.page }} of {{ d.num_pages }} · {{ d.count }} row{{
                    d.count === 1 ? '' : 's'
                  }}
                </span>
                <button
                  mat-stroked-button
                  type="button"
                  (click)="nextPage()"
                  [disabled]="!d.has_next"
                >
                  Next
                </button>
              </div>
            }
          }
        }
      }
    </section>
  </div>
</div>
